import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { QuickSuggestions } from "./QuickSuggestions";
import { MarkdownMessage } from "./MarkdownMessage";
import { Conversation, Message, ObraOption } from "./AIAssistant";
import {
  ArrowRight,
  Bot,
  Building2,
  CheckCircle2,
  Copy,
  FileText,
  Info,
  PanelLeft,
  Send,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";

interface ChatAreaProps {
  conversation: Conversation;
  isLoading: boolean;
  onSendMessage: (message: string, options?: { detailLevel?: "concise" | "detailed" }) => void;
  showSuggestions?: boolean;
  onSuggestionClick?: (suggestion: string) => void;
  /** Obras do usuário para o seletor de contexto do cabeçalho. */
  obras: ObraOption[];
  obraId: string | null;
  onObraChange: (id: string) => void;
  /** Abre o drawer de histórico no mobile. */
  onOpenHistory?: () => void;
}

const MessageBubble = ({ message }: { message: Message }) => {
  const isUser = message.type === "user";

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      toast({ title: "Resposta copiada" });
    } catch {
      toast({
        title: "Não foi possível copiar",
        description: "Seu navegador bloqueou o acesso à área de transferência.",
        variant: "destructive",
      });
    }
  };

  if (isUser) {
    return (
      <div className="mb-6 flex justify-end">
        <div className="max-w-[85%] rounded-lg bg-primary px-3.5 py-2.5 text-sm text-primary-foreground md:max-w-[42rem]">
          <div className="whitespace-pre-wrap break-words">{message.content}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 flex gap-3">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
        <Bot className="h-4 w-4" />
      </div>

      <div className="flex min-w-0 max-w-[85%] flex-col gap-2 md:max-w-[42rem]">
        <MarkdownMessage content={message.content} />

        {/* Ações executadas pela IA no sistema */}
        {message.actions?.map((action, index) => (
          <div
            key={index}
            className="flex flex-wrap items-center gap-3 rounded-lg border border-success/40 bg-success/[0.08] px-3.5 py-3"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-success text-white">
              <CheckCircle2 className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold">{action.description}</span>
              <span className="block text-xs text-muted-foreground">
                Registrado no sistema
              </span>
            </span>
            <Button variant="ghost" size="sm" asChild>
              <a href="/tarefas">
                Abrir no Gestor
                <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
          </div>
        ))}

        <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
          {message.source && (
            <span className="inline-flex items-center gap-1.5 text-xs">
              <FileText className="h-3.5 w-3.5" />
              {message.source}
            </span>
          )}
          <div className="ml-auto flex gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={copiar}
              aria-label="Copiar resposta"
              title="Copiar resposta"
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => toast({ title: "Obrigado pelo retorno!" })}
              aria-label="Resposta útil"
              title="Resposta útil"
            >
              <ThumbsUp className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() =>
                toast({
                  title: "Retorno registrado",
                  description: "Vamos usar isso para melhorar as respostas.",
                })
              }
              aria-label="Resposta com problema"
              title="Resposta com problema"
            >
              <ThumbsDown className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TypingIndicator = () => (
  <div className="mb-6 flex gap-3">
    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
      <Bot className="h-4 w-4" />
    </div>
    <div className="flex items-center gap-1 rounded-lg bg-muted px-3.5 py-3">
      <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/50" style={{ animationDelay: "0ms" }} />
      <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/50" style={{ animationDelay: "150ms" }} />
      <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/50" style={{ animationDelay: "300ms" }} />
    </div>
  </div>
);

export const ChatArea = ({
  conversation,
  isLoading,
  onSendMessage,
  showSuggestions = false,
  onSuggestionClick,
  obras,
  obraId,
  onObraChange,
  onOpenHistory,
}: ChatAreaProps) => {
  const [inputValue, setInputValue] = useState("");
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [detailLevel, setDetailLevel] = useState<"concise" | "detailed">("detailed");

  // Auto-scroll ao chegar mensagem nova
  useEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]"
    );
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
  }, [conversation.messages, isLoading]);

  const handleSend = () => {
    if (inputValue.trim() && !isLoading) {
      onSendMessage(inputValue.trim(), { detailLevel });
      setInputValue("");
      textareaRef.current?.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const ultimaMensagem = conversation.messages[conversation.messages.length - 1];
  const respostaCortada =
    ultimaMensagem?.type === "ai" && ultimaMensagem.finishReason === "length";
  const obraAtual = obras.find((o) => o.id === obraId);

  return (
    <div className="grid h-full grid-rows-[auto_1fr_auto]">
      {/* Cabeçalho — mesmo padrão do ListPageLayout das telas de cadastro */}
      <header className="flex items-center justify-between gap-3 border-b border-border bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex min-w-0 items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onOpenHistory}
            aria-label="Abrir histórico de conversas"
            className="h-11 w-11 shrink-0 md:hidden"
          >
            <PanelLeft className="h-5 w-5" />
          </Button>

          <h1 className="shrink-0 text-lg font-semibold text-foreground sm:text-xl">
            Obra IA
          </h1>

          {obras.length > 0 && (
            <Select value={obraId ?? undefined} onValueChange={onObraChange}>
              <SelectTrigger className="w-auto min-w-0 max-w-[45vw] gap-2 md:max-w-xs">
                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                <SelectValue placeholder="Selecione a obra" />
              </SelectTrigger>
              <SelectContent>
                {obras.map((obra) => (
                  <SelectItem key={obra.id} value={obra.id}>
                    {obra.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </header>

      {/* Mensagens */}
      <ScrollArea ref={scrollAreaRef} className="min-h-0">
        <div className="mx-auto max-w-3xl px-4 py-6">
          {showSuggestions && conversation.messages.length === 0 && onSuggestionClick ? (
            <>
              <div className="mb-6">
                <h2 className="mb-1.5 text-2xl font-semibold tracking-tight">
                  {obraAtual
                    ? `Como posso ajudar na ${obraAtual.nome}?`
                    : "Como posso ajudar hoje?"}
                </h2>
                <p className="max-w-prose text-muted-foreground">
                  Pergunte sobre normas técnicas e custos, cruze os dados dos seus
                  RDOs ou peça uma ação — a Obra IA cria tarefas direto no Gestor.
                </p>
              </div>

              <QuickSuggestions onSuggestionClick={onSuggestionClick} />

              <p className="mt-4 flex items-center gap-2.5 rounded-lg border border-dashed border-border px-3.5 py-3 text-sm text-muted-foreground">
                <Info className="h-4 w-4 shrink-0 text-primary" />
                As respostas usam os dados reais desta conta — obras, RDOs e cadastros.
              </p>
            </>
          ) : (
            <>
              {conversation.messages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))}

              {respostaCortada && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onSendMessage("Continue", { detailLevel })}
                >
                  Continuar resposta
                </Button>
              )}

              {isLoading && <TypingIndicator />}
            </>
          )}
        </div>
      </ScrollArea>

      {/* Composer */}
      <div className="border-t border-border bg-background px-4 pb-3 pt-3">
        <div className="mx-auto flex max-w-3xl flex-col gap-2">
          <div className="flex items-center gap-3">
            <ToggleGroup
              type="single"
              size="sm"
              value={detailLevel}
              onValueChange={(v) => v && setDetailLevel(v as "concise" | "detailed")}
              aria-label="Nível de detalhe da resposta"
            >
              <ToggleGroupItem className="h-11 sm:h-9" value="concise">Concisa</ToggleGroupItem>
              <ToggleGroupItem className="h-11 sm:h-9" value="detailed">Detalhada</ToggleGroupItem>
            </ToggleGroup>
            <span className="ml-auto hidden text-xs text-muted-foreground sm:block">
              Enter envia · Shift+Enter quebra linha
            </span>
          </div>

          <div className="flex items-end gap-2 rounded-lg border border-border bg-background p-1 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
            <Textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Pergunte sobre normas, custos e suas obras — ou peça uma ação…"
              aria-label="Mensagem para a Obra IA"
              className={cn(
                "min-h-11 max-h-32 flex-1 resize-none border-0 bg-transparent px-2.5 py-2.5",
                "focus-visible:ring-0 focus-visible:ring-offset-0"
              )}
              disabled={isLoading}
            />
            <Button
              onClick={handleSend}
              disabled={!inputValue.trim() || isLoading}
              size="icon"
              className="h-11 w-11 shrink-0"
              aria-label="Enviar mensagem"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>

          <p className="text-center text-[11px] text-muted-foreground">
            A Obra IA pode errar — confira dados críticos antes de decidir.
          </p>
        </div>
      </div>
    </div>
  );
};
